// ============================================================================
// Toasts and modals — the replacements for alert(), confirm(), and prompt().
//
// The old admin ran its entire workflow through native dialogs: adjusting
// points, logging a sale, denying an application. They block the page, can't
// be styled, and on mobile they look like a browser error. Everything here is
// keyboard-accessible and focus-trapped.
// ============================================================================

import { html, raw } from './html.js';

// ------------------------------------------------------------------ toasts --

export function toast(message, kind = 'info', ms = 4200) {
  const host = document.getElementById('dw-toasts');
  if (!host) return;

  const el = document.createElement('div');
  el.className = `dw-toast dw-toast--${kind}`;
  el.innerHTML = String(html`
    <span class="dw-toast-body">${message}</span>
    <button class="dw-toast-close" aria-label="Dismiss">&times;</button>
  `);

  const remove = () => {
    el.classList.add('is-leaving');
    el.addEventListener('transitionend', () => el.remove(), { once: true });
    setTimeout(() => el.remove(), 400);
  };

  el.querySelector('.dw-toast-close').addEventListener('click', remove);
  host.appendChild(el);
  requestAnimationFrame(() => el.classList.add('is-in'));
  if (ms) setTimeout(remove, ms);
}

export const toastOk    = (m) => toast(m, 'success');
export const toastError = (m) => toast(m, 'error', 6000);

// ------------------------------------------------------------------ modals --

let openModal = null;

/**
 * Show a modal. `body` is trusted markup you built with html``.
 * Returns a promise resolving to the value passed to close(), or null if the
 * user dismissed it.
 */
export function modal({ title, body, actions = [], size = '' }) {
  closeModal();

  return new Promise((resolve) => {
    const root = document.getElementById('dw-modal-root');
    const wrap = document.createElement('div');
    wrap.className = 'dw-modal-backdrop';

    wrap.innerHTML = String(html`
      <div class="dw-modal ${size}" role="dialog" aria-modal="true" aria-label="${title}">
        <div class="dw-modal-head">
          <h2 class="dw-h5">${title}</h2>
          <button class="dw-modal-x" data-dismiss aria-label="Close">&times;</button>
        </div>
        <div class="dw-modal-body">${raw(body)}</div>
        <div class="dw-modal-foot">
          ${actions.map((a, i) => html`
            <button class="dw-btn ${a.className ?? 'dw-btn--secondary'}" data-action="${i}">
              ${a.label}
            </button>`)}
        </div>
      </div>
    `);

    const previouslyFocused = document.activeElement;

    const done = (value) => {
      wrap.remove();
      document.removeEventListener('keydown', onKey);
      openModal = null;
      previouslyFocused?.focus?.();
      resolve(value);
    };

    const onKey = (e) => {
      if (e.key === 'Escape') return done(null);
      if (e.key !== 'Tab') return;
      // Focus trap: keep Tab inside the dialog.
      const focusables = wrap.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    wrap.addEventListener('click', (e) => {
      if (e.target === wrap || e.target.hasAttribute('data-dismiss')) return done(null);

      const idx = e.target.getAttribute('data-action');
      if (idx === null) return;
      const action = actions[Number(idx)];
      // onClick may return a value, a promise, or false to keep the modal open.
      Promise.resolve(action.onClick?.(wrap)).then((result) => {
        if (result !== false) done(result === undefined ? (action.value ?? true) : result);
      });
    });

    document.addEventListener('keydown', onKey);
    root.appendChild(wrap);
    openModal = wrap;

    requestAnimationFrame(() => {
      wrap.classList.add('is-in');
      wrap.querySelector('input, textarea, select, .dw-btn--primary, button')?.focus();
    });
  });
}

export function closeModal() {
  openModal?.remove();
  openModal = null;
}

/** Replaces window.confirm. Destructive actions get a red button. */
export function confirmDialog({ title, message, confirmLabel = 'Confirm', danger = false }) {
  return modal({
    title,
    body: html`<p class="dw-body">${message}</p>`,
    actions: [
      { label: 'Cancel', className: 'dw-btn--ghost', value: false },
      {
        label: confirmLabel,
        className: danger ? 'dw-btn--danger' : 'dw-btn--primary',
        value: true
      }
    ]
  }).then((v) => v === true);
}

// ------------------------------------------------------------- form helpers --

/** Put a form into a submitting state so nobody double-fires a mutation. */
export function setBusy(button, busy, busyLabel = 'Working…') {
  if (!button) return;
  if (busy) {
    button.dataset.label = button.textContent;
    button.textContent = busyLabel;
    button.disabled = true;
  } else {
    if (button.dataset.label) button.textContent = button.dataset.label;
    button.disabled = false;
  }
}

export function inlineAlert(message, kind = 'error') {
  return html`<div class="dw-alert dw-alert--${kind}">${message}</div>`;
}

/** Skeleton rows for async views, so layout doesn't jump when data lands. */
export function skeleton(rows = 3) {
  return raw(
    Array.from({ length: rows }, () => '<div class="dw-skeleton"></div>').join('')
  );
}
